import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, ConfirmDialog, Skeleton, Toggle, useToast } from '@bbpos/ui';
import { siteService } from '@/services/site.service';
import { apiClientPaginated } from '@/lib/api-client';
import { GLASS_PANEL, KpiStatCard, cn } from './tenant-ui';
import { SITE_PAGE_KEYS, SITE_PAGE_LABELS } from '@/types/site';
import { TenantDomainPanel } from './TenantDomainPanel';
import { TenantContentModerationPanel } from './TenantContentModerationPanel';
import type { SitePageSeo, SlugAvailability, TenantSite } from '@/types/site';

/** Shape we need from /locations; mirrors TenantLocationsTab's LocationRow. */
interface TenantLocationRow {
  id: string;
  name: string;
  is_active?: boolean;
}

const SITE_DOMAIN = 'shop.orio360.com';
const TITLE_LIMIT = 60;
const DESCRIPTION_LIMIT = 160;

/** Enough of a catalogue that a site is not obviously thin. Advisory only —
 *  indexing is a manual admin decision, this just informs it. */
const READY_PRODUCTS = 15;
const READY_IMAGES = 8;

function GlobeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" />
    </svg>
  );
}
function BoxIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 8l-9-5-9 5 9 5 9-5zM3 8v8l9 5 9-5V8" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  );
}

export function TenantWebsiteTab({
  tenantId,
  isAdminPlus,
  isSuperAdmin,
}: {
  tenantId: string;
  isAdminPlus: boolean;
  isSuperAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const siteQuery = useQuery({
    queryKey: ['tenant-site', tenantId],
    queryFn: () => siteService.get(tenantId),
    // A tenant with no website is the normal starting state, not an error.
    retry: false,
  });
  const site = siteQuery.data;
  const notAllowlisted = siteQuery.isError && !site;

  const pagesQuery = useQuery({
    queryKey: ['tenant-site-pages', tenantId],
    queryFn: () => siteService.listPages(tenantId),
    enabled: Boolean(site),
  });

  const locationsQuery = useQuery({
    queryKey: ['tenant-locations', tenantId],
    queryFn: async () =>
      (await apiClientPaginated<TenantLocationRow[]>(
        `/locations?tenant_id=${tenantId}&limit=50`,
      )).data,
    enabled: isAdminPlus,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['tenant-site', tenantId] });
    void queryClient.invalidateQueries({ queryKey: ['tenant-site-pages', tenantId] });
  };

  const patch = useMutation({
    mutationFn: (payload: Parameters<typeof siteService.update>[1]) =>
      siteService.update(tenantId, payload),
    onSuccess: () => {
      invalidate();
      toast.toast({ variant: 'success', title: 'Website updated' });
    },
    onError: (err: Error) => toast.toast({ variant: 'error', title: err.message || 'Could not update the website' }),
  });

  if (siteQuery.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <div className="animate-slide-up space-y-6">
      {!isAdminPlus && (
        <Alert
          variant="info"
          title="View only"
          description="Only ADMIN and SUPER_ADMIN can change a tenant's website."
        />
      )}

      {notAllowlisted ? (
        <SetupPanel tenantId={tenantId} isAdminPlus={isAdminPlus} onDone={invalidate} />
      ) : (
        site && (
          <>
            <StatusBanner site={site} />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <KpiStatCard
                label="Status"
                value={site.is_enabled ? 'Published' : 'Not published'}
                supporting={
                  site.is_enabled
                    ? site.allow_indexing
                      ? 'Listed on Google'
                      : 'Hidden from Google'
                    : 'Visitors see a 404'
                }
                tone={site.is_enabled ? (site.allow_indexing ? 'success' : 'warning') : 'info'}
                icon={<GlobeIcon />}
              />
              <KpiStatCard
                label="Public products"
                value={site.public_product_count}
                supporting={`${site.products_with_images} with photos`}
                tone="teal"
                icon={<BoxIcon />}
              />
              <KpiStatCard
                label="SEO overrides"
                value={site.seo_override_count}
                supporting={`of ${SITE_PAGE_KEYS.length} pages customised`}
                tone="violet"
                icon={<SearchIcon />}
              />
            </div>

            <AddressPanel site={site} isAdminPlus={isAdminPlus} onDone={invalidate} />

            <VisibilityPanel
              site={site}
              isAdminPlus={isAdminPlus}
              locations={locationsQuery.data ?? []}
              onPatch={(payload) => patch.mutate(payload)}
              pending={patch.isPending}
            />

            <SeoPanel
              tenantId={tenantId}
              site={site}
              pages={pagesQuery.data ?? []}
              isAdminPlus={isAdminPlus}
              onDone={invalidate}
            />

            {/* Phase 1. Sits under the site config because a domain points at
                the website — the API rejects one for a tenant with no site. */}
            <TenantDomainPanel
              tenantId={tenantId}
              isAdminPlus={isAdminPlus}
              isSuperAdmin={isSuperAdmin}
              hasWebsite
            />

            {/* Phase 3. The per-page hold, which is the only lever we have
                between "index the whole site" and "index none of it" — and the
                only one at all against a tenant publishing spam on a hostname
                inside the registrable domain every tenant shares. */}
            <TenantContentModerationPanel tenantId={tenantId} canModerate={isAdminPlus} />

            {isSuperAdmin && (
              <RemovePanel tenantId={tenantId} slug={site.slug} onDone={invalidate} />
            )}
          </>
        )
      )}
    </div>
  );
}

// ─── Status banner ───────────────────────────────────────────────────────────

function StatusBanner({ site }: { site: TenantSite }) {
  const toast = useToast();
  const tone = site.is_enabled
    ? 'from-teal-600 to-emerald-600'
    : 'from-neutral-600 to-neutral-700';

  return (
    <div className={cn('rounded-xl bg-gradient-to-r p-6 text-white shadow-lg', tone)}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm/relaxed opacity-90">Public website</p>
          <p className="mt-1 break-all font-mono text-lg font-semibold">{site.live_url}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Badge variant={site.is_enabled ? 'success' : 'neutral'}>
              {site.is_enabled ? 'Live' : 'Allowlisted, not published'}
            </Badge>
            {site.is_enabled && !site.allow_indexing && (
              <Badge variant="warning">Not indexed</Badge>
            )}
            {site.hide_prices && <Badge variant="neutral">Prices hidden</Badge>}
            {!site.primary_location_active && <Badge variant="danger">Location inactive</Badge>}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard?.writeText(site.live_url);
              toast.toast({ variant: 'success', title: 'Address copied' });
            }}
          >
            Copy
          </Button>
          <Button variant="secondary" onClick={() => window.open(site.live_url, '_blank')}>
            Open
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Setup (no site yet) ─────────────────────────────────────────────────────

function SetupPanel({
  tenantId,
  isAdminPlus,
  onDone,
}: {
  tenantId: string;
  isAdminPlus: boolean;
  onDone: () => void;
}) {
  const toast = useToast();
  const [slug, setSlug] = useState('');
  const [locationId, setLocationId] = useState('');
  const availability = useSlugAvailability(slug);

  const locationsQuery = useQuery({
    queryKey: ['tenant-locations', tenantId],
    queryFn: async () =>
      (await apiClientPaginated<TenantLocationRow[]>(
        `/locations?tenant_id=${tenantId}&limit=50`,
      )).data,
    enabled: isAdminPlus,
  });
  const locations = locationsQuery.data ?? [];

  const create = useMutation({
    mutationFn: () => siteService.create(tenantId, { slug, primary_location_id: locationId }),
    onSuccess: () => {
      onDone();
      toast.toast({ variant: 'success', title: 'Website allowlisted' });
    },
    onError: (err: Error) => toast.toast({ variant: 'error', title: err.message || 'Could not allowlist the website' }),
  });

  return (
    <div className={cn(GLASS_PANEL, 'p-6 animate-fade-in')}>
      <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
        No website for this tenant
      </h3>
      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
        Allowlisting creates the site but does not publish it. Nothing is visible to the public
        until you switch on <strong>Publish website</strong>, and nothing reaches Google until you
        also switch on <strong>Allow indexing</strong>.
      </p>

      {isAdminPlus ? (
        <div className="mt-5 space-y-4">
          <SlugField slug={slug} onChange={setSlug} availability={availability} />

          <label className="block">
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              Catalogue location
            </span>
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-800 dark:text-white"
            >
              <option value="">Select a location…</option>
              {locations.map((l: { id: string; name: string; is_active?: boolean }) => (
                <option key={l.id} value={l.id} disabled={l.is_active === false}>
                  {l.name}
                  {l.is_active === false ? ' (inactive)' : ''}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-neutral-500 dark:text-neutral-400">
              Products are stored per location, so the site publishes exactly one store&apos;s
              catalogue.
            </span>
          </label>

          <Button
            variant="primary"
            disabled={!availability?.available || !locationId || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Creating…' : 'Allowlist website'}
          </Button>
        </div>
      ) : (
        <Alert
          variant="info"
          title="View only"
          description="Ask an ADMIN to set up a website for this tenant."
          className="mt-4"
        />
      )}
    </div>
  );
}

// ─── Address ─────────────────────────────────────────────────────────────────

function AddressPanel({
  site,
  isAdminPlus,
  onDone,
}: {
  site: TenantSite;
  isAdminPlus: boolean;
  onDone: () => void;
}) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [slug, setSlug] = useState(site.slug);
  const [confirming, setConfirming] = useState(false);
  const availability = useSlugAvailability(editing && slug !== site.slug ? slug : '');

  const rename = useMutation({
    mutationFn: () => siteService.setSlug(site.tenant_id, slug),
    onSuccess: () => {
      setEditing(false);
      setConfirming(false);
      onDone();
      toast.toast({ variant: 'success', title: 'Address changed' });
    },
    onError: (err: Error) => toast.toast({ variant: 'error', title: err.message || 'Could not change the address' }),
  });

  return (
    <div className={cn(GLASS_PANEL, 'p-6 animate-fade-in')}>
      <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Address</h3>

      {!editing ? (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <code className="rounded-lg bg-neutral-100 px-3 py-2 text-sm dark:bg-neutral-800 dark:text-neutral-200">
            {site.slug}.{SITE_DOMAIN}
          </code>
          {isAdminPlus && (
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Change address
            </Button>
          )}
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <SlugField slug={slug} onChange={setSlug} availability={availability} />
          <div className="flex gap-2">
            <Button
              variant="primary"
              disabled={slug === site.slug || !availability?.available || rename.isPending}
              onClick={() => (site.is_enabled ? setConfirming(true) : rename.mutate())}
            >
              Save address
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setSlug(site.slug);
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/*
        With no redirect and reusable slugs, this dialog is the only thing
        between an admin and an irreversible loss — so it states both
        consequences plainly rather than asking a vague "are you sure?".
      */}
      <ConfirmDialog
        open={confirming}
        variant="danger"
        title="Change the public address?"
        description={
          `${site.slug}.${SITE_DOMAIN} will stop working and becomes available for any other ` +
          `tenant to claim. Search rankings for this site start from zero. There is no redirect.`
        }
        confirmLabel="Change address"
        onConfirm={() => rename.mutate()}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}

// ─── Visibility ──────────────────────────────────────────────────────────────

function VisibilityPanel({
  site,
  isAdminPlus,
  locations,
  onPatch,
  pending,
}: {
  site: TenantSite;
  isAdminPlus: boolean;
  locations: { id: string; name: string; is_active?: boolean }[];
  onPatch: (payload: Parameters<typeof siteService.update>[1]) => void;
  pending: boolean;
}) {
  const [unpublishing, setUnpublishing] = useState(false);
  const ready =
    site.public_product_count >= READY_PRODUCTS &&
    site.products_with_images >= READY_IMAGES &&
    site.has_address &&
    site.has_phone;

  return (
    <div className={cn(GLASS_PANEL, 'p-6 animate-fade-in space-y-5')}>
      <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Visibility</h3>

      <ToggleRow
        label="Publish website"
        description={
          'When off, visitors get a 404. This is different from a suspended tenant, which gets a ' +
          '“temporarily unavailable” page so its search rankings survive.'
        }
        checked={site.is_enabled}
        disabled={!isAdminPlus || pending}
        onChange={(next) => (next ? onPatch({ is_enabled: true }) : setUnpublishing(true))}
      />

      <ToggleRow
        label="Allow indexing"
        description={
          site.allow_indexing
            ? 'Search engines may list this site.'
            : 'Search engines are told not to list this site. Turn on once the generated content looks right.'
        }
        checked={site.allow_indexing}
        disabled={!isAdminPlus || pending}
        onChange={(allow_indexing) => onPatch({ allow_indexing })}
      >
        {!ready && (
          <Alert
            variant="warning"
            title="Looks thin for search"
            className="mt-2"
            description={
              <ul className="list-disc pl-4 text-xs">
                {site.public_product_count < READY_PRODUCTS && (
                  <li>
                    {READY_PRODUCTS - site.public_product_count} more products (
                    {site.public_product_count} of {READY_PRODUCTS})
                  </li>
                )}
                {site.products_with_images < READY_IMAGES && (
                  <li>
                    {READY_IMAGES - site.products_with_images} more product photos (
                    {site.products_with_images} of {READY_IMAGES})
                  </li>
                )}
                {!site.has_address && <li>Store address is missing</li>}
                {!site.has_phone && <li>Store phone is missing</li>}
              </ul>
            }
          />
        )}
      </ToggleRow>

      <ToggleRow
        label="Show prices"
        description="Turn off for B2B or quote-on-request catalogues. Hiding prices forfeits product rich results in Google."
        checked={!site.hide_prices}
        disabled={!isAdminPlus || pending}
        onChange={(show) => onPatch({ hide_prices: !show })}
      />

      <ToggleRow
        label="Show stock availability"
        description="Only enable for tenants who keep stock quantities accurate — others will show every product as out of stock."
        checked={site.show_stock_status}
        disabled={!isAdminPlus || pending}
        onChange={(show_stock_status) => onPatch({ show_stock_status })}
      />

      <label className="block">
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
          Catalogue location
        </span>
        <select
          value={site.primary_location_id}
          disabled={!isAdminPlus || pending}
          onChange={(e) => onPatch({ primary_location_id: e.target.value })}
          className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm disabled:opacity-60 dark:border-neutral-700 dark:bg-neutral-800 dark:text-white"
        >
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
              {l.is_active === false ? ' (inactive)' : ''}
            </option>
          ))}
        </select>
      </label>

      {!site.primary_location_active && (
        <Alert
          variant="danger"
          title="Catalogue location is inactive"
          description="The website is serving a “temporarily unavailable” page. Pick an active location to bring it back."
        />
      )}

      <ConfirmDialog
        open={unpublishing}
        variant="warning"
        title="Unpublish this website?"
        description={
          'Visitors and search engines will get a 404, and the site will be dropped from search ' +
          'results. To pause without losing rankings, suspend the tenant instead.'
        }
        confirmLabel="Unpublish"
        onConfirm={() => {
          onPatch({ is_enabled: false });
          setUnpublishing(false);
        }}
        onCancel={() => setUnpublishing(false)}
      />
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  disabled,
  onChange,
  children,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-neutral-100 pb-4 last:border-0 dark:border-neutral-800">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-neutral-900 dark:text-white">{label}</p>
        <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">{description}</p>
        {children}
      </div>
      <Toggle checked={checked} disabled={disabled} onChange={onChange} />
    </div>
  );
}

// ─── SEO ─────────────────────────────────────────────────────────────────────

function SeoPanel({
  tenantId,
  site,
  pages,
  isAdminPlus,
  onDone,
}: {
  tenantId: string;
  site: TenantSite;
  pages: SitePageSeo[];
  isAdminPlus: boolean;
  onDone: () => void;
}) {
  return (
    <div className={cn(GLASS_PANEL, 'p-6 animate-fade-in space-y-5')}>
      <div>
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
          Search engine listing
        </h3>
        <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
          Leave a field empty to use the text generated from the tenant&apos;s own data. The
          preview shows what Google would display.
        </p>
      </div>

      {SITE_PAGE_KEYS.map((key) => (
        <SeoPageCard
          key={key}
          tenantId={tenantId}
          site={site}
          pageKey={key}
          existing={pages.find((p) => p.page_key === key) ?? null}
          isAdminPlus={isAdminPlus}
          onDone={onDone}
        />
      ))}
    </div>
  );
}

function SeoPageCard({
  tenantId,
  site,
  pageKey,
  existing,
  isAdminPlus,
  onDone,
}: {
  tenantId: string;
  site: TenantSite;
  pageKey: string;
  existing: SitePageSeo | null;
  isAdminPlus: boolean;
  onDone: () => void;
}) {
  const toast = useToast();
  const [title, setTitle] = useState(existing?.meta_title ?? '');
  const [description, setDescription] = useState(existing?.meta_description ?? '');
  const [noindex, setNoindex] = useState(existing?.noindex ?? false);

  useEffect(() => {
    setTitle(existing?.meta_title ?? '');
    setDescription(existing?.meta_description ?? '');
    setNoindex(existing?.noindex ?? false);
  }, [existing]);

  const dirty =
    title !== (existing?.meta_title ?? '') ||
    description !== (existing?.meta_description ?? '') ||
    noindex !== (existing?.noindex ?? false);

  const save = useMutation({
    mutationFn: () =>
      siteService.upsertPage(tenantId, pageKey, {
        meta_title: title || undefined,
        meta_description: description || undefined,
        noindex,
      }),
    onSuccess: () => {
      onDone();
      toast.toast({ variant: 'success', title: 'Listing updated' });
    },
    onError: (err: Error) => toast.toast({ variant: 'error', title: err.message || 'Could not save' }),
  });

  const reset = useMutation({
    mutationFn: () => siteService.clearPage(tenantId, pageKey),
    onSuccess: () => {
      onDone();
      toast.toast({ variant: 'success', title: 'Reverted to generated text' });
    },
    onError: (err: Error) => toast.toast({ variant: 'error', title: err.message || 'Could not reset' }),
  });

  const path = pageKey === 'home' ? '' : `/${pageKey}`;

  return (
    <div className="rounded-xl border border-neutral-200 p-4 dark:border-neutral-700">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-neutral-900 dark:text-white">
          {SITE_PAGE_LABELS[pageKey] ?? pageKey}
        </p>
        {existing && <Badge variant="info">Customised</Badge>}
      </div>

      <div className="mt-3 grid gap-3">
        <CountedInput
          label="Title"
          value={title}
          limit={TITLE_LIMIT}
          disabled={!isAdminPlus}
          onChange={setTitle}
          placeholder={`${site.slug} — generated from tenant data`}
        />
        <CountedInput
          label="Description"
          value={description}
          limit={DESCRIPTION_LIMIT}
          disabled={!isAdminPlus}
          onChange={setDescription}
          multiline
          placeholder="Generated from categories, city and product count"
        />
      </div>

      {/* Google-style preview — the only way to judge the generated copy. */}
      <div className="mt-4 rounded-lg bg-neutral-50 p-3 dark:bg-neutral-800/60">
        <p className="truncate text-xs text-emerald-700 dark:text-emerald-400">
          {site.live_url}
          {path}
        </p>
        <p className="mt-0.5 truncate text-base text-[#1a0dab] dark:text-sky-400">
          {title || `${SITE_PAGE_LABELS[pageKey] ?? pageKey} — generated title`}
        </p>
        <p className="mt-0.5 line-clamp-2 text-xs text-neutral-600 dark:text-neutral-400">
          {description || 'Generated description will appear here.'}
        </p>
      </div>

      {isAdminPlus && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button variant="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
          {existing && (
            <Button variant="secondary" disabled={reset.isPending} onClick={() => reset.mutate()}>
              Reset to auto
            </Button>
          )}
          <label className="ml-auto flex items-center gap-2 text-xs text-neutral-600 dark:text-neutral-400">
            <input
              type="checkbox"
              checked={noindex}
              onChange={(e) => setNoindex(e.target.checked)}
            />
            Hide this page from search
          </label>
        </div>
      )}
    </div>
  );
}

function CountedInput({
  label,
  value,
  limit,
  disabled,
  onChange,
  placeholder,
  multiline,
}: {
  label: string;
  value: string;
  limit: number;
  disabled: boolean;
  onChange: (next: string) => void;
  placeholder?: string;
  multiline?: boolean;
}) {
  const over = value.length > limit;
  const cls =
    'mt-1 w-full rounded-lg border px-3 py-2 text-sm dark:bg-neutral-800 dark:text-white ' +
    (over
      ? 'border-amber-400 dark:border-amber-500'
      : 'border-neutral-300 dark:border-neutral-700');
  return (
    <label className="block">
      <span className="flex items-center justify-between text-sm font-medium text-neutral-700 dark:text-neutral-300">
        {label}
        <span
          className={cn(
            'text-xs tabular-nums',
            over ? 'text-amber-600 dark:text-amber-400' : 'text-neutral-400',
          )}
        >
          {value.length}/{limit}
        </span>
      </span>
      {multiline ? (
        <textarea
          rows={2}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={cls}
        />
      ) : (
        <input
          type="text"
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={cls}
        />
      )}
    </label>
  );
}

// ─── Remove ──────────────────────────────────────────────────────────────────

function RemovePanel({
  tenantId,
  slug,
  onDone,
}: {
  tenantId: string;
  slug: string;
  onDone: () => void;
}) {
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () => siteService.remove(tenantId),
    onSuccess: () => {
      setConfirming(false);
      onDone();
      toast.toast({ variant: 'success', title: 'Website removed' });
    },
    onError: (err: Error) => toast.toast({ variant: 'error', title: err.message || 'Could not remove the website' }),
  });

  return (
    <div className={cn(GLASS_PANEL, 'p-6 animate-fade-in')}>
      <h3 className="text-lg font-semibold text-rose-600 dark:text-rose-400">Remove website</h3>
      <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
        Deletes the site and its SEO overrides, and frees <code>{slug}</code> for any other tenant
        to claim.
      </p>
      <Button variant="danger" className="mt-4" onClick={() => setConfirming(true)}>
        Remove website
      </Button>
      <ConfirmDialog
        open={confirming}
        variant="danger"
        title="Remove this website?"
        description={`The site goes offline immediately and ${slug} becomes claimable by another tenant. SEO overrides are lost.`}
        confirmLabel="Remove"
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}

// ─── Slug field ──────────────────────────────────────────────────────────────

const PROBLEM_COPY: Record<string, string> = {
  TAKEN: 'Already taken',
  RESERVED: 'Reserved word',
  FORMAT: 'Letters, numbers and single hyphens only',
  LENGTH: 'Between 3 and 63 characters',
  PUNYCODE: 'Cannot start with xn--',
};

function SlugField({
  slug,
  onChange,
  availability,
}: {
  slug: string;
  onChange: (next: string) => void;
  availability: SlugAvailability | null;
}) {
  return (
    <div>
      <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Address</span>
      <div className="mt-1 flex items-stretch">
        <span className="flex items-center rounded-l-lg border border-r-0 border-neutral-300 bg-neutral-50 px-3 text-sm text-neutral-500 dark:border-neutral-700 dark:bg-neutral-800">
          https://
        </span>
        <input
          type="text"
          value={slug}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Website address"
          className="min-w-0 flex-1 border-y border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-800 dark:text-white"
          placeholder="acme-traders"
        />
        <span className="flex items-center rounded-r-lg border border-l-0 border-neutral-300 bg-neutral-50 px-3 text-sm text-neutral-500 dark:border-neutral-700 dark:bg-neutral-800">
          .{SITE_DOMAIN}
        </span>
      </div>

      {slug.length > 0 && availability && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {availability.available ? (
            <Badge variant="success">Available</Badge>
          ) : (
            <>
              <Badge variant={availability.reason === 'TAKEN' ? 'danger' : 'warning'}>
                {PROBLEM_COPY[availability.reason ?? ''] ?? 'Not usable'}
              </Badge>
              {availability.suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => onChange(s)}
                  className="rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700 transition-all duration-200 hover:bg-brand-100 dark:bg-brand-900/30 dark:text-brand-300"
                >
                  {s}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Debounced availability lookup. Advisory — submit can still 409. */
function useSlugAvailability(slug: string): SlugAvailability | null {
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(slug), 400);
    return () => clearTimeout(t);
  }, [slug]);

  const query = useQuery({
    queryKey: ['slug-available', debounced],
    queryFn: () => siteService.checkSlug(debounced),
    enabled: debounced.trim().length > 0,
    retry: false,
  });

  return useMemo(() => query.data ?? null, [query.data]);
}
